import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useExperienceWizard } from 'core/contexts/experienceWizard';
import { getRatesOptions } from '@utriper/core';
import { Field, Form } from 'react-final-form';
import { Box, FormHelperText, Grid2 } from '@mui/material';
import Title from 'modules/Pricing/components/Title';
import ActionButtons from 'modules/Pricing/PriceGroupWizard/components/ActionButtons';
import { Container, Label } from '../../styles';
import { Autocomplete, NumberInput, Switch } from '@utriper/core-ui';

const PriceForm = () => {
    const { t } = useTranslation();
    const translate = (key) => t(`experience_form.${key}`);
    const { stepData, handleBack, handleNext, updateStepData, STEP_KEYS } = useExperienceWizard();

    const currencyOptions = getRatesOptions();

    const handleSubmit = (values) => {
        updateStepData(STEP_KEYS.STEP_3, values);
        handleNext();
    };

    const validateForm = (values) => {
        const errors = {};

        const price = values?.price ? parseFloat(values.price) : null;
        const duration = values?.duration ? parseFloat(values.duration) : null;

        if (values.allowOpenDateBooking) {
            if (!Number.isFinite(price)) {
                errors.price = t('common_form.required');
            }

            if (!Number.isInteger(duration)) {
                errors.duration = t('common_form.required');
            }

            if (!values?.currency) {
                errors.currency = t('common_form.required');
            }
        }

        if (price && (!Number.isFinite(price) || price < 0)) {
            errors.price = translate('errors.invalid_price');
        }

        if (Number.isFinite(price) && !values?.currency) {
            errors.currency = translate('errors.missing_currency');
        }

        if (values.currency && !Number.isFinite(price)) {
            errors.price = translate('errors.missing_price');
        }

        if (duration && (!Number.isInteger(duration) || duration <= 0)) {
            errors.duration = translate('errors.invalid_duration');
        }

        return errors;
    };

    return (
        <Form
            onSubmit={handleSubmit}
            validate={validateForm}
            initialValues={stepData[STEP_KEYS.STEP_3]}
            render={({ handleSubmit, form, values }) => {
                const handleActive = (event) => {
                    const checked = event?.target?.checked;

                    const newValues = { allowOpenDateBooking: checked };
                    form.initialize(newValues);
                };

                return (
                    <form onSubmit={handleSubmit} noValidate style={{ width: '100%', maxWidth: '480px' }}>
                        <Box component="section">
                            <Title title={translate('price_data.title')} description={translate('price_data.description')} />

                            {/* Form */}
                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                                <Container
                                    sx={{
                                        '& .MuiSwitch-switchBase': {
                                            '&.Mui-checked': {
                                                color: '#fff',
                                                '& + .MuiSwitch-track': {
                                                    backgroundColor: '#137DC5',
                                                    opacity: 1,
                                                },
                                            },
                                        },
                                    }}
                                >
                                    <Field type="checkbox" name="allowOpenDateBooking" component={Switch} label={translate('allow_open_date_booking')} input={{ checked: values?.allowOpenDateBooking ?? false, onChange: handleActive }} />
                                </Container>

                                <Fragment>
                                    {/* Price */}
                                    <Container>
                                        <Label>{translate('price')}</Label>
                                        <Field name="price" component={NumberInput} placeholder={translate('price_placeholder')} disabled={!values.allowOpenDateBooking} />
                                    </Container>

                                    {/* Currency */}
                                    <Container>
                                        <Label>{translate('currency')}</Label>
                                        <Field name="currency" component={Autocomplete} options={currencyOptions} label={translate('currency_placeholder')} fullWidth disabled={!values.allowOpenDateBooking} />
                                    </Container>

                                    {/* Duration */}
                                    <Container>
                                        <Label>{translate('duration')}</Label>
                                        <Field name="duration" component={NumberInput} placeholder={translate('duration_placeholder')} disabled={!values.allowOpenDateBooking} />
                                    </Container>

                                    <FormHelperText>{translate('price_helper')}</FormHelperText>
                                </Fragment>
                            </Grid2>

                            <ActionButtons
                                handleSubmit={(e) => {
                                    return handleSubmit(e);
                                }}
                                handleBack={handleBack}
                            />
                        </Box>
                    </form>
                );
            }}
        />
    );
};

export default PriceForm;
